import { Injectable } from '@angular/core';
import { Building } from '../building/building.interface';
import { Cluster } from '../cluster/cluster.interface';
import { Subject, BehaviorSubject } from 'rxjs';
import { Project } from './project.interface';
import { ConvertResourceToJsonPipe } from '../../utilities/convert-resource-to-json/convert-resource-to-json.pipe';
import { first } from 'rxjs/operators';
import { Area } from '../area/area.interface';
import { ChaptersService } from '../../utilities/chapters/chapters.service';
import { PveFilterPipe } from '../../utilities/pve/pve-filter.pipe';
import { ProjectElement } from '../element/project-element.interface';
import { ProjectModule } from '../module/project-module.interface';
import { ProjectSubmodule } from '../submodule/project-submodule.interface';
import { BasicArea } from '../basic-area/basic-area.interface';

/**
 * Project core service.
 *
 * Contains all the private logic for Project management.
 *
 * @todo Create seperate project stores to speed up application:
 *  - project$
 *  - projectModule$
 *  - projectBuilding$
 *  - projectBasicArea$
 * @todo Set up ProjectLocationService with getEntityLocation(object) and getEntityIndex(id).
 *
 * **Only ProjectService is allowed to access this Service.**
 */
@Injectable({
  providedIn: 'root'
})
export class ProjectCoreService {

  /** Project subject. */
  protected _project: Subject<Project> = new BehaviorSubject<Project>(null);

  /** Project observable. */
  public project$ = this._project.asObservable();

  /** Constructor. */
  constructor(
    protected chaptersService: ChaptersService,
    protected convertResourceToJson: ConvertResourceToJsonPipe,
    protected pveFilterPipe: PveFilterPipe
  ) { }


  /**
   * Get project.
   *
   * @returns The current active project.
   */
  public get(): Project {
    let _project: Project;
    this.project$.pipe(first()).subscribe(p => _project = p);
    return _project;
  }

  /**
   * Set project.
   *
   * Save the given project to the Project Observable.
   *
   * @todo Find out why convertResourceToJson completely stalls the browser.
   * @todo Is called twice on init (project info page), why?
   *
   * @param project Project to set.
   * @param [init=false] Whether the set is an init set.
   */
  public set(project: Project, init: boolean = false): void {
    if (project) {
      this._pipeline(project, init);
      this._project.next(project);
    }
  }

   /**
   * Get the location (path) of the area within the project.
   *
   * @param building The area to find.
   * @returns The location of the area.
   */
  protected _getAreaLocation(area: Area): string {
    const project = this.get();
    const buildingIndex = project.buildings.findIndex(b => b._id === area.buildingId);
    const building = project.buildings[buildingIndex];
    const clusterIndex = building.clusters.findIndex(c => c._id === area.clusterId);
    const cluster = building.clusters[clusterIndex];
    const areaIndex = cluster.areas.findIndex(a => a._id === area._id);
    return `buildings[${ buildingIndex }].clusters[${ clusterIndex }].areas[${ areaIndex }]`;
  }

  /**
   * Get the location (path) of the basic area within the project.
   *
   * @todo Move to ProjectLocationService.
   *
   * @param basicArea The basic area to find.
   * @returns The location of the basic area.
   */
  protected _getBasicAreaLocation(basicArea: BasicArea): string {
    const project = this.get();
    const basicAreaIndex = project.basicAreas.findIndex(ba => ba._id === basicArea._id);
    return `basicAreas[${ basicAreaIndex }]`;
  }

  /**
   * Get the location (path) of the building within the project.
   *
   * @todo Move to ProjectLocationService.
   *
   * @param building The building to find.
   * @returns The location of the building.
   */
  protected _getBuildingLocation(building: Building): string {
    const project = this.get();
    const buildingIndex = project.buildings.findIndex(b => b._id === building._id);
    return `buildings[${ buildingIndex }]`;
  }

  /**
   * Get the location (path) of the cluster within the project.
   *
   * @todo Move to ProjectLocationService.
   *
   * @param cluster The cluster to find.
   * @returns The location of the cluster.
   */
  protected _getClusterLocation(cluster: Cluster): string {
    const project = this.get();
    const buildingIndex = project.buildings.findIndex(b => b._id === cluster.buildingId);
    const clusterIndex = project.buildings[buildingIndex].clusters.findIndex(c => c._id === cluster._id);
    return `buildings[${ buildingIndex }].clusters[${ clusterIndex }]`;
  }

  /**
   * Get the location (path) of the element within the project.
   *
   * @todo Move to ProjectLocationService.
   *
   * @param element The element to find.
   * @returns The location of the element.
   */
  protected _getElementLocation(element: ProjectElement): string {
    const project = this.get();
    const moduleIndex = project.modules.findIndex(m => m._id === element.moduleId);
    const module = project.modules[moduleIndex];
    const submoduleIndex = module.submodules.findIndex(s => s._id === element.submoduleId);
    const submodule = module.submodules[submoduleIndex];
    const elementIndex = submodule.elements.findIndex(e =>
      e.active
      && e._id === element._id
      && e.identifier === element.identifier
      && e.projectLevel === element.projectLevel
      && e.constructionId === element.constructionId);
    return `modules[${ moduleIndex }].submodules[${ submoduleIndex }].elements[${ elementIndex }]`;
  }

  /**
   * Get the location (path) of the module within the project.
   *
   * @todo Move to ProjectLocationService.
   *
   * @param module The module to find.
   * @returns The location of the module.
   */
  protected _getModuleLocation(module: ProjectModule): string {
    const project = this.get();
    const moduleIndex = project.modules.findIndex(m => m._id === module._id);
    return `modules[${ moduleIndex }]`;
  }

  /**
   * Get the location (path) of the submodule within the project.
   *
   * @todo Move to ProjectLocationService.
   *
   * @param submodule The submodule to find.
   * @returns The location of the submodule.
   */
  protected _getSubmoduleLocation(submodule: ProjectSubmodule): string {
    const project = this.get();
    const moduleIndex = project.modules.findIndex(m => m._id === submodule.moduleId);
    const module = project.modules[moduleIndex];
    const submoduleIndex = module.submodules.findIndex(s => s._id === submodule._id);
    return `modules[${ moduleIndex }].submodules[${ submoduleIndex }]`;
  }

  /**
   * Pipeline for Project data.
   *
   * On init and on every update to the project data, the data is sent through this pipeline.
   * When a call is heavy, and only needed for init, use the init param.
   *
   * @param project The project data.
   * @param [init] Can be used to only call a method on init.
   */
  private _pipeline(project: Project, init?: boolean): void {
    if (init) {
      this._pipelinePatchElements(project);
      this._pipelineEnrichSubmodules(project);
      // this._pipelineMoveDeletedModules(project);
      // this._pipelineMoveDeletedSubmodules(project);
      this._pipelineMoveDeletedElements(project);
    }
    this._pipelineEnrichElements(project);
    this._pipelineHideWhenEmpty(project);
    this._pipelineChapters(project);
  }

  /**
   * Add chapters numbers on each project update.
   *
   * @param project The project data.
   */
  private _pipelineChapters(project: Project): void {
    project.modules = this.chaptersService.chapterize(project.modules);
  }

/**
   * Enrich elements.
   *
   * - Recalculate element code on each update cycle.
   *
   * @param project The project data.
   */
  private _pipelineEnrichElements(project: Project): void {
    for (const module of project.modules) {
      for (const submodule of module.submodules) {
        for (const element of submodule.elements) {
          element.code = `${ project.key }-${ element.identifier }`;
        }
      }
    }
  }

  /**
   * Enrich submodules.
   *
   * @param project The project data.
   */
  private _pipelineEnrichSubmodules(project: Project): void {
    for (const module of project.modules) {
      for (const submodule of module.submodules) {
        submodule.moduleId = module._id;
      }
    }
  }

  /**
   * Makes sure all empty or inactive modules/submodules/elements get `hide: true`, so the
   * templates and controllers receive correct data.
   *
   * @param project The project data.
   */
  private _pipelineHideWhenEmpty(project: Project): void {
    project.modules = this.pveFilterPipe.transform(project.modules);
  }

  /**
   * Move inactive/empty elements to the end.
   *
   * - Inactive.
   * - No elements.
   *
   * @todo Let the backend do this upon returning project data.
   *
   * @param project The project to move the elements for.
   */
  private _pipelineMoveDeletedElements(project: Project): void {
    for (const module of project.modules) {
      for (const submodule of module.submodules) {
        const active = [];
        const inactive = [];
        for (const element of submodule.elements) {
          element.active
            ? active.push(element)
            : inactive.push(element);
        }
        submodule.elements = active.concat(inactive);
      }
    }
  }

  /**
   * Move inactive/empty modules to the end.
   *
   * - Inactive.
   * - No submodules.
   *
   * @todo Let the backend do this upon returning project data.
   *
   * @param project The project to move the modules for.
   */
  private _pipelineMoveDeletedModules(project: Project): void {
    const active = [];
    const inactive = [];
    for (const module of project.modules) {
      module.active && module.submodules.length
        ? active.push(module)
        : inactive.push(module);
    }
    project.modules = active.concat(inactive);
  }

  /**
   * Move inactive/empty submodules to the end.
   *
   * - Inactive.
   * - No elements.
   *
   * @todo Let the backend do this upon returning project data.
   *
   * @param project The project to move the submodules for.
   */
  private _pipelineMoveDeletedSubmodules(project: Project): void {
    for (const module of project.modules) {
      const active = [];
      const inactive = [];
      for (const submodule of module.submodules) {
        submodule.active && submodule.elements.length
          ? active.push(submodule)
          : inactive.push(submodule);
      }
      module.submodules = active.concat(inactive);
    }
  }

  /**
   * Patch elements.
   *
   * Fixes:
   *  - Remove two statusObject properties which can cause a Circular JSON error.
   *  - Replace status object with status id.
   *
   * @param project The project data.
   */
  private _pipelinePatchElements(project: Project): void {
    for (const module of project.modules) {
      for (const submodules of module.submodules) {
        for (const element of submodules.elements) {
          delete element.statusObj;
          delete element.statusObject;
          delete element.$$hashKey;
        }
      }
    }
  }
}
